import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTemplates } from '../../hooks/useTemplates';

export const useEditExperimentForm = ({ experiment, templates }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_id: '',
    treatments: [],
    traffic_allocation: 'equal_split',
    success_metrics: [],
    minimum_sample_size: 100,
    statistical_significance: 0.95,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [templateVersions, setTemplateVersions] = useState([]);

  const { getTemplateVersions } = useTemplates();

  // Check if experiment is active (restricts certain field updates)
  const isActive = experiment?.status === 'active';

  // Valid traffic allocation values
  const validTrafficAllocations = useMemo(
    () => ['equal_split', 'weighted_split', 'single_treatment'],
    []
  );

  // Safe traffic allocation value that matches available options
  const safeTrafficAllocation = useMemo(() => {
    return validTrafficAllocations.includes(formData.traffic_allocation)
      ? formData.traffic_allocation
      : '';
  }, [formData.traffic_allocation, validTrafficAllocations]);

  // Get the template name for display
  const templateName = useMemo(() => {
    if (!formData.template_id) {
      return 'No template assigned';
    }
    // Try to find template in the templates list
    const template = templates.find(t => t.id === formData.template_id);
    if (template) {
      return template.name;
    }
    // If not found, use experiment's template_name if available
    if (experiment?.template_name) {
      return experiment.template_name;
    }
    // Fallback to template ID
    return `Template ${formData.template_id.substring(0, 8)}...`;
  }, [formData.template_id, templates, experiment]);

  const selectedTemplate = templates.find(t => t.id === formData.template_id);

  // Helper function to get safe template version value
  const getSafeTemplateVersion = useCallback(
    treatmentVersion => {
      if (!treatmentVersion && treatmentVersion !== 0) return '';
      const versionNum =
        typeof treatmentVersion === 'number'
          ? treatmentVersion
          : parseInt(treatmentVersion);
      if (isNaN(versionNum)) return '';
      return templateVersions.some(v => parseInt(v.version) === versionNum)
        ? versionNum
        : '';
    },
    [templateVersions]
  );

  const loadTemplateVersions = useCallback(
    async templateId => {
      try {
        const versionsData = await getTemplateVersions(templateId);
        const versions = versionsData.templates || [];
        setTemplateVersions(versions);
      } catch (error) {
        setTemplateVersions([]);
      }
    },
    [getTemplateVersions]
  );

  // Initialize form data when experiment changes
  useEffect(() => {
    if (experiment) {
      setFormData({
        name: experiment.name || '',
        description: experiment.description || '',
        template_id: experiment.template_id || '',
        treatments: experiment.treatments || [],
        traffic_allocation: experiment.traffic_allocation || 'equal_split',
        success_metrics: experiment.success_metrics || [],
        minimum_sample_size: experiment.minimum_sample_size || 100,
        statistical_significance: experiment.statistical_significance || 0.95,
      });
      setErrors({});

      // Load template versions if template_id is available
      if (experiment.template_id) {
        loadTemplateVersions(experiment.template_id);
      }
    }
  }, [experiment, loadTemplateVersions]);

  // Load template versions when template changes
  useEffect(() => {
    if (formData.template_id) {
      loadTemplateVersions(formData.template_id);
    }
  }, [formData.template_id, loadTemplateVersions]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTreatmentChange = (index, field, value) => {
    const newTreatments = [...formData.treatments];
    newTreatments[index] = {
      ...newTreatments[index],
      [field]: value,
    };
    setFormData(prev => ({
      ...prev,
      treatments: newTreatments,
    }));
  };

  const addTreatment = () => {
    const newTreatment = {
      name: `Variant ${formData.treatments.length + 1}`,
      description: `Variant ${formData.treatments.length + 1} treatment`,
      treatment_type: 'treatment',
      template_version: '1',
      template_modifications: {},
      hypothesis: '',
      tags: [],
    };

    setFormData(prev => ({
      ...prev,
      treatments: [...prev.treatments, newTreatment],
    }));
  };

  const removeTreatment = index => {
    if (formData.treatments.length > 1) {
      const newTreatments = formData.treatments.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        treatments: newTreatments,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Experiment name is required';
    }

    if (!formData.template_id) {
      newErrors.template_id = 'Please select a template';
    }

    if (formData.treatments.length < 2) {
      newErrors.treatments = 'At least 2 treatments are required';
    }

    formData.treatments.forEach((treatment, index) => {
      if (!treatment.name.trim()) {
        newErrors[`treatment_${index}_name`] = 'Treatment name is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    submitting,
    setSubmitting,
    isActive,
    safeTrafficAllocation,
    templateName,
    selectedTemplate,
    templateVersions,
    getSafeTemplateVersion,
    handleInputChange,
    handleTreatmentChange,
    addTreatment,
    removeTreatment,
    validateForm,
  };
};
